"use client";

import { useState } from "react";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

type Tab = {
  id: string;
  label: string;
  editable: ReturnType<typeof storyblokEditable>;
  content: React.ReactNode;
};

// Every panel stays in the DOM (toggled with `hidden`) so all content is server-rendered and indexable.
export default function TabbedModuleView({ blok, tabs }: { blok: SbBlokData; tabs: Tab[] }) {
  const [active, setActive] = useState(0);

  if (tabs.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(2rem,4vw,3.5rem)]">
        <div
          role="tablist"
          className="flex flex-wrap gap-2.5 border-b border-black/10 pb-5 sm:gap-5 sm:pb-6"
        >
          {tabs.map((tab, i) => {
            const isActive = i === active;
            return (
              <button
                key={tab.id}
                {...tab.editable}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(i)}
                className={`sm:text-body inline-flex h-9 cursor-pointer items-center justify-center rounded-full px-5 text-[12px] font-bold transition-colors sm:h-12 sm:px-7 ${
                  isActive
                    ? "bg-forest-02 text-white"
                    : "border-forest-02 text-forest-02 hover:bg-forest-02/5 border-2 bg-white"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/*
        Keying by active state remounts a panel when it's switched away from,
        which unmounts any playing video (iframe) so it stops — a hidden
        panel alone would keep playing in the background.
      */}
      {tabs.map((tab, i) => (
        <div key={`${tab.id}-${i === active}`} role="tabpanel" hidden={i !== active}>
          {tab.content}
        </div>
      ))}
    </section>
  );
}
